// Client for the Supabase `booking` Edge Function. Same functions as
// mockBackend.js, so the form does not know which one it is talking to.
const ENDPOINT = process.env.GATSBY_BOOKING_API
// Public by design: the Turnstile site key sits in the page, the secret key only
// in the Supabase function.
const TURNSTILE_SITE_KEY =
  process.env.GATSBY_TURNSTILE_SITE_KEY || "0x4AAAAAAFDReUADEm1jEit9"
const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const VISITOR_KEY = "fi-booking-visitor"

let visitorId = null

const call = async (action, payload = {}) => {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, visitorId, ...payload }),
  })
  const json = await res.json().catch(() => ({ ok: false, error: "network" }))
  if (!res.ok && json.error !== "invalid_code") {
    throw new Error(json.error || `HTTP ${res.status}`)
  }
  return json
}

export const getVisitorId = () => {
  const fresh = `v-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
  try {
    visitorId = window.localStorage.getItem(VISITOR_KEY)
    if (!visitorId) {
      visitorId = fresh
      window.localStorage.setItem(VISITOR_KEY, fresh)
    }
  } catch (e) {
    visitorId = fresh
  }
  return visitorId
}

export const recordEvent = (id, type, data = {}) =>
  call("event", {
    type,
    value: data.value || data.answer,
    values: data.values,
  }).catch(() => null)

export const createLead = async (id, details) => {
  const res = await call("saveLead", {
    details,
    page: window.location.pathname,
    locale: document.documentElement.lang,
  })
  return { ok: true, leadId: res.leadId }
}

// The server derives stages itself; only the photo step needs telling.
export const updateLead = async (leadId, patch) => {
  if (patch.stage === "photos_received") await call("photosDone")
  return { ok: true }
}

// The server decides the route: a facelift lead can come back as Dr. Faris's
// consultation when Dr. Schmidt has no intro call within a month.
export const fetchSlots = async (route, consultMode, firstStep) => {
  const res = await call("availability", { consultMode, firstStep })
  return {
    route: res.route,
    slots: res.slots || [],
    noSchmidtCalls: res.noSchmidtCalls,
  }
}

export const uploadPhotos = async (leadId, files) => {
  for (const [slot, file] of Object.entries(files)) {
    if (!file) continue
    const { url } = await call("photoUploadUrl", {
      slot,
      contentType: file.type,
    })
    const put = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "image/jpeg" },
      body: file,
    })
    if (!put.ok) throw new Error(`upload ${slot}`)
  }
  return { ok: true }
}

let turnstileLoading = null
let turnstileWidget = null
let pendingToken = null

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (!turnstileLoading) {
    turnstileLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = TURNSTILE_SCRIPT
      script.async = true
      script.onload = () => resolve(window.turnstile)
      script.onerror = () => reject(new Error("turnstile_load"))
      document.head.appendChild(script)
    })
  }
  return turnstileLoading
}

// The widget is invisible: Cloudflare decides in the background whether this is
// a person. Tokens are single-use, so a fresh one is requested for every SMS.
const turnstileToken = async () => {
  const turnstile = await loadTurnstile()
  return new Promise((resolve, reject) => {
    pendingToken = { resolve, reject }
    if (turnstileWidget === null) {
      const container = document.createElement("div")
      container.style.display = "none"
      document.body.appendChild(container)
      turnstileWidget = turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        execution: "execute",
        callback: (token) => pendingToken && pendingToken.resolve(token),
        "error-callback": () =>
          pendingToken && pendingToken.reject(new Error("turnstile")),
      })
    } else {
      turnstile.reset(turnstileWidget)
    }
    turnstile.execute(turnstileWidget)
  })
}

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    ),
  ])

export const requestAppointment = async (leadId, booking) => {
  const token = await withTimeout(turnstileToken(), 15000).catch(() => null)
  await call("request", { ...booking, turnstileToken: token })
  return { ok: true, requestId: "server" }
}

export const verifyAppointment = (requestId, code) => call("verify", { code })

export const resendCode = () => call("resend")

export const changeMobileNumber = (requestId, mobile) =>
  call("changeNumber", { mobile })
